import React, {useState, useCallback, useEffect, useRef} from 'react';
import cx from 'classnames';
import {ImageMode} from '@wix/wixstores-client-core/dist/es/src/media/constants';
import {IMediaItem} from '../../../../types/app-types';
import {ProductImage} from '../../../ProductGallery/MainMedia/ProductImage/ProductImage';
import {ModalGalleryLayout} from '../../../ModalGallery/ModalGalleryLayout';
import s from './GridGallery.scss';
import {DEFAULT_IMAGE_SIZE} from '../../../../constants';
import {DefaultProductImage} from '@wix/wixstores-client-common-components/dist/es/src/DefaultProductImage/DefaultProductImage';
import {ProvidedGlobalProps, withGlobalProps} from '../../../../providers/globalPropsProvider';

const WIXVIDEO_BASEURL = 'https://video.wixstatic.com/';
const PAGE_SIZE = 12;

export enum GridGalleryDataHook {
  GridGallery = 'grid-gallery',
  ImageGrid = 'grid-gallery-image-grid',
  ImageWrapper = 'grid-gallery-image-wrapper',
  Video = 'grid-gallery-video',
  NoMedia = 'grid-gallery-no-media',
  InfiniteScrollSentinel = 'grid-gallery-infinite-sentinel',
}

export interface GridGalleryProps extends ProvidedGlobalProps {
  media: IMediaItem[];
  productName: string;
  imageMode: ImageMode;
}

const GridGalleryComponent: React.FC<GridGalleryProps> = ({
  media,
  productName,
  imageMode,
  globals,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [visibleCount, setVisibleCount] = useState<number>(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  /* istanbul ignore next */
  const loadNextPage = useCallback(() => {
    setVisibleCount((prev) => Math.min(media.length, prev + PAGE_SIZE));
  }, [media.length]);

  /* istanbul ignore next */
  useEffect(() => {
    if (!sentinelRef.current) {
      return;
    }
    if (visibleCount >= media.length) {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadNextPage();
          }
        });
      },
      {
        threshold: 0.1,
      }
    );

    observerRef.current.observe(sentinelRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [visibleCount, media.length, loadNextPage]);

  const handleFocus = useCallback((index: number) => {
    setSelectedIndex(index);
  }, []);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
    }
  }, []);

  const isVideoMedia = useCallback((mediaItem: IMediaItem): boolean => {
    return mediaItem.mediaType === 'VIDEO' && mediaItem.videoFiles && mediaItem.videoFiles.length > 0;
  }, []);

  const renderVideo = (mediaItem: IMediaItem): JSX.Element => {
    const video = mediaItem.videoFiles && mediaItem.videoFiles[0];
    const src = `${WIXVIDEO_BASEURL}${video.url}#t=0.001`;
    return (
      <video
        data-hook={GridGalleryDataHook.Video}
        className={s.video}
        controls
        controlsList="nodownload"
        src={src}
        poster={mediaItem.url}
        preload="metadata"
        aria-label={mediaItem.altText}
      />
    );
  };

  if (!media || media.length === 0) {
    return (
      <DefaultProductImage
        imageSize={DEFAULT_IMAGE_SIZE}
        className={s.noMedia}
        dataHook={GridGalleryDataHook.NoMedia}
        isDynamicSize={globals.experiments.EnableDynamicSizeDefaultImage}
      />
    );
  }

  return (
    <div className={s.gridGallery} data-hook={GridGalleryDataHook.GridGallery}>
      <ModalGalleryLayout externalSelectedIndex={selectedIndex}>
        {(modalZoom) => (
          <div className={s.imageGrid} data-hook={GridGalleryDataHook.ImageGrid}>
            {media.map((mediaItem, index) => (
              <div
                key={index}
                data-hook={`${GridGalleryDataHook.ImageWrapper}-${index}`}
                className={cx(s.imageWrapper, {
                  [s.selected]: selectedIndex === index,
                  'modal-zoom-icon': modalZoom.isEnabled,
                  [s.hidden]: index >= visibleCount,
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  modalZoom.open();
                }}
                onFocus={() => handleFocus(index)}
                onKeyDown={(e) => handleKeyDown(e)}
                role="button"
                tabIndex={0}
                aria-label={`View ${isVideoMedia(mediaItem) ? 'video' : 'image'} ${index + 1} of ${media.length}: ${
                  mediaItem.altText
                }`}>
                {isVideoMedia(mediaItem) ? (
                  renderVideo(mediaItem)
                ) : (
                  <ProductImage
                    mediaItem={mediaItem}
                    imageMode={imageMode}
                    productName={productName}
                    fluid={true}
                    stretchImage={imageMode === 1}
                    isSelected={selectedIndex === index}
                    isZoomModalEnabled={modalZoom.isEnabled}
                    onExpandIconEnterKeyPress={modalZoom.open}
                  />
                )}
              </div>
            ))}
            <div data-hook={GridGalleryDataHook.InfiniteScrollSentinel} ref={sentinelRef} />
          </div>
        )}
      </ModalGalleryLayout>
    </div>
  );
};

export const GridGallery = withGlobalProps(GridGalleryComponent);
